/**
 * Query keys of the TanStack Query cache over the engine (web.md §3). Every screen builds its keys
 * here so that `invalidate.ts` can reach them: the FIRST element names what changes together.
 *
 *   ['list', query]            messages.list pages (useInfiniteQuery), any scope
 *   ['counts']                 messages.counts (sidebar)
 *   ['message', id]            messages.get / open (Details, reader)
 *   ['corrections', …]         corrections.list
 *   ['folders'] ['tags'] ['topics', …] ['sources', …] ['rules', kind] ['views'] ['connectors']
 *   ['digests', …] ['settings']                       entity lists and synced settings
 *   ['localSettings'] ['sourceStatus'] ['sourceStats', id] ['plan', …] ['pipeline'] ['sync']
 *   ['workspace', …]                                  device-local reads (extensions of the table)
 *   ['models', …]                                     models.list / get / catalog, and ['models', 'installs']
 *                                                     (this device's downloads; `model` events)
 */

import type { ListQuery, MessageId, ModelId, RuleKind, SourceId } from '@digester/core';

export type EntityKey = 'folders' | 'tags' | 'topics' | 'sources' | 'rules' | 'views' | 'connectors' | 'digests' | 'settings';

export const qk = {
  lists: () => ['list'] as const,
  list: (query: Omit<ListQuery, 'cursor'>) => ['list', query] as const,
  counts: () => ['counts'] as const,
  messages: () => ['message'] as const,
  message: (id: MessageId) => ['message', id] as const,
  corrections: (query?: Record<string, unknown>) => (query ? (['corrections', query] as const) : (['corrections'] as const)),
  folders: () => ['folders'] as const,
  tags: () => ['tags'] as const,
  topics: (options?: { includeArchived?: boolean }) => (options ? (['topics', options] as const) : (['topics'] as const)),
  sources: (options?: { includeArchived?: boolean }) => (options ? (['sources', options] as const) : (['sources'] as const)),
  source: (id: SourceId) => ['sources', 'one', id] as const,
  rules: (kind?: RuleKind) => (kind ? (['rules', kind] as const) : (['rules'] as const)),
  views: () => ['views'] as const,
  connectors: () => ['connectors'] as const,
  digests: (page?: Record<string, unknown>) => (page ? (['digests', page] as const) : (['digests'] as const)),
  settings: () => ['settings'] as const,
  localSettings: () => ['localSettings'] as const,
  sourceStatus: () => ['sourceStatus'] as const,
  sourceStats: (id?: SourceId) => (id ? (['sourceStats', id] as const) : (['sourceStats'] as const)),
  /** sources.overview(): under sourceStats (source_days changes) and refreshed by message changes too. */
  sourceOverview: () => ['sourceStats', 'overview'] as const,
  plan: (part?: 'entitlement' | 'usage') => (part ? (['plan', part] as const) : (['plan'] as const)),
  pipeline: () => ['pipeline'] as const,
  sync: () => ['sync'] as const,
  workspace: (part?: 'stats') => (part ? (['workspace', part] as const) : (['workspace'] as const)),
  /** models.list(); everything about models lives under ['models'] so one event refreshes it all. */
  models: () => ['models'] as const,
  model: (id: ModelId) => ['models', 'one', id] as const,
  modelCatalog: () => ['models', 'catalog'] as const,
  /** models.installs(): this device's downloads (refreshed by every `model` event). */
  modelInstalls: () => ['models', 'installs'] as const,
};
